import React, { useState, useEffect } from 'react';
import QuizGenerator from './QuizGenerator';
import QuizHistory from './QuizHistory';
import QuizBattles from './QuizBattles';
import { FiBook, FiClock, FiAward, FiUsers } from 'react-icons/fi';

function QuizRepository() {
  const [activeTab, setActiveTab] = useState('intro'); // 'intro', 'generate', 'history', 'battles'
  const [initialBattleId, setInitialBattleId] = useState(null);

  // Check URL for battleId on mount and set active tab
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const battleId = urlParams.get('battleId');
    if (battleId) {
      setActiveTab('battles');
      setInitialBattleId(battleId);
    }
  }, []); // Run only once on mount

  const tabs = [
    { id: 'intro', label: 'Introduction', icon: FiBook },
    { id: 'generate', label: 'Generate Quiz', icon: FiAward },
    { id: 'battles', label: 'Quiz Battles', icon: FiUsers },
    { id: 'history', label: 'Quiz History', icon: FiClock }
  ];

  const Introduction = () => (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-[rgb(130,88,18)] mb-4">Welcome to Intellectual Oasis Fellowship Quiz Repository</h1>
        <p className="text-gray-600 text-lg">
          Your comprehensive platform for creating, taking, and competing in educational quizzes
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
          <h2 className="text-xl font-semibold text-[rgb(130,88,18)] mb-4">About the Repository</h2>
          <p className="text-gray-600 mb-4">
            The Intellectual Oasis Fellowship Quiz Repository is a powerful tool designed to enhance your learning experience through interactive quizzes and competitive battles. Our platform offers a comprehensive suite of features to help you test your knowledge, track your progress, and compete with peers.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
          <h2 className="text-xl font-semibold text-[rgb(130,88,18)] mb-4">Key Features</h2>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-start">
              <FiAward className="w-5 h-5 text-[rgb(130,88,18)] mt-1 mr-2" />
              <span>Generate custom quizzes based on subjects, topics, and difficulty levels</span>
            </li>
            <li className="flex items-start">
              <FiClock className="w-5 h-5 text-[rgb(130,88,18)] mt-1 mr-2" />
              <span>Track your quiz history and monitor your progress over time</span>
            </li>
            <li className="flex items-start">
              <FiUsers className="w-5 h-5 text-[rgb(130,88,18)] mt-1 mr-2" />
              <span>Compete in real-time quiz battles with other learners</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
        <h2 className="text-xl font-semibold text-[rgb(130,88,18)] mb-4">How to Use</h2>
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-lg mb-2">1. Generate Quizzes</h3>
            <p className="text-gray-600">
              Create personalized quizzes by selecting your preferred subject, topic, and difficulty level. You can choose the number of questions and set time limits to match your study needs.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-2">2. Track Progress</h3>
            <p className="text-gray-600">
              Review your quiz history to see your performance over time. Analyze your strengths and areas for improvement to focus your study efforts effectively.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-lg mb-2">3. Join Battles</h3>
            <p className="text-gray-600">
              Participate in real-time quiz battles to test your knowledge against other learners. Compete for top positions on the leaderboard and earn recognition for your achievements.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-[rgb(130,88,18)] bg-opacity-5 p-6 rounded-lg border border-[rgb(130,88,18)] border-opacity-20">
        <h2 className="text-xl font-semibold text-[rgb(130,88,18)] mb-4">Tips for Success</h2>
        <ul className="space-y-3 text-gray-600">
          <li>• Start with easier difficulty levels and gradually increase the challenge</li>
          <li>• Review your quiz results to identify areas needing improvement</li>
          <li>• Participate regularly in quiz battles to enhance your competitive skills</li>
          <li>• Use the quiz history to track your progress and set learning goals</li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 rounded-lg shadow-lg bg-white">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 overflow-x-auto" style={{ MsOverflowStyle: 'none', scrollbarWidth: 'none' }}>
        <nav className="-mb-px flex space-x-4 md:space-x-8" aria-label="Tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`flex-shrink-0 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-b-2 border-[rgb(130,88,18)] text-[rgb(130,88,18)]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'intro' && <Introduction />}
        {activeTab === 'generate' && <QuizGenerator />}
        {activeTab === 'battles' && <QuizBattles initialBattleId={initialBattleId} />}
        {activeTab === 'history' && <QuizHistory />}
      </div>
    </div>
  );
}

export default QuizRepository;
